import { classes } from '@theme/constants';
import { getAttributeValue } from '@theme/dom';
import { isFromBreakpoint } from '@theme/helpers';
import { HTMLShopifySectionElement } from '@theme/html-custom-element';

class ProductGuides extends HTMLShopifySectionElement {
  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    if (this.refs.slider) {
      this.on('swiperautoplaytimeleft', this.refs.slider, this.handleSliderAutoplayTimeLeft.bind(this));
      this.on('swiperautoplaystart', this.refs.slider, this.handleSliderAutoplayStart.bind(this));
      this.on('swiperautoplaystop', this.refs.slider, this.handleSliderAutoplayStop.bind(this));

      /**
       * Configure the sliders.
       */
      for (const [index, slider] of this.refs.slider.all.entries()) {
        Object.assign(slider, {
          loop: true,
          speed: 500,
          autoplay:
            /**
             * Only enable autoplay for the first slider.
             */
            index === 0
              ? getAttributeValue(this, 'autoplay', false)
                ? {
                    delay: getAttributeValue(this, 'autoplay-delay', 3000),
                  }
                : false
              : false,
          effect: 'fade',
          fadeEffect: {
            crossFade: true,
          },
        });

        this.on('swiperslidechange', slider, this.handleSlideChange.bind(this));

        if (typeof slider.initialize === 'function') {
          slider.initialize();
        }
      }

      if (this.refs.thumbnail) {
        for (const thumbnail of this.refs.thumbnail.all) {
          this.on('mouseover', thumbnail, ({ currentTarget }) => {
            if (isFromBreakpoint('large')) {
              this.refs.slider.swiper.slideToLoop(Number(currentTarget.dataset.index0));
            }
          });
        }
      }
    }
  }

  /**
   * Handles the slide change event.
   * @param {object} event - The event payload.
   * @param {HTMLElement} event.target - The target element.
   * @param {{ realIndex: number }[]} event.detail - The event detail.
   */
  handleSlideChange({ target, detail: [{ realIndex }] }) {
    /**
     * Sync sibling sliders.
     */
    for (const slider of this.refs.slider.all) {
      if (!(slider === target) && slider.swiper && !(slider.swiper.realIndex === realIndex)) {
        slider.swiper.slideToLoop(realIndex);
      }
    }

    /**
     * Update thumbnails.
     */
    for (const thumbnail of this.refs.thumbnail.all) {
      requestAnimationFrame(() => {
        thumbnail.classList.toggle(classes.active, Number(thumbnail.dataset.index0) === realIndex);
      });
    }
  }

  /**
   * Handles the slider autoplay time left event.
   * @param {Event} event - The event payload.
   * @param {object} event.detail
   */
  handleSliderAutoplayTimeLeft({ detail: [, , progress] }) {
    this.style.setProperty('--progress', `${((1 - progress) * 100).toFixed(1)}%`);
  }

  /**
   * Handles the slider autoplay start event.
   */
  handleSliderAutoplayStart() {
    this.classList.add(classes.playing);
  }

  /**
   * Handles the slider autoplay stop event.
   */
  handleSliderAutoplayStop() {
    this.classList.remove(classes.playing);
  }

  onBlockSelect(event) {
    this.refs.slider.swiper.slideToLoop(Number(event.target.dataset.index0), 0);

    if (this.refs.slider.swiper.autoplay.running) {
      this.refs.slider.swiper.autoplay.pause();
    }
  }

  onBlockDeselect() {
    if (this.refs.slider.swiper.autoplay.running) {
      this.refs.slider.swiper.autoplay.resume();
    }
  }

  onSectionLoad() {
    for (const slider of this.refs.slider.all) {
      slider.initialize();
    }
  }
}

if (!customElements.get('product-guides')) customElements.define('product-guides', ProductGuides);
